<template>
  <section class="buy-domain">
    <div class="buy-domain__head">
      <div>
        <h2 class="h6 mb-0">Buy a domain through us</h2>
        <p class="text-muted small mb-0">
          Search for a domain and we'll register, host and email-configure it for
          you. Every order is reviewed by a human before we buy — no surprise spend.
        </p>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger py-2 small mt-2">{{ error }}</div>

    <!-- Search -->
    <div class="bd-search d-flex gap-2 align-items-start mt-3">
      <input
        v-model="query"
        type="text"
        class="form-control form-control-sm"
        placeholder="yourbrand  (or yourbrand.co.uk)"
        @keyup.enter="doSearch"
      >
      <button class="btn btn-primary btn-sm text-nowrap" :disabled="busy.search || !query.trim()" @click="doSearch">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.search" />
        <CygnusIcon name="search" class="me-1" v-else />Search
      </button>
    </div>

    <p v-if="searched && registrarLive === false" class="bd-note small mt-2">
      <CygnusIcon name="info-circle" class="me-1" />Live availability isn't configured yet
      (sandbox). Prices are indicative; ordering still records your request for an
      operator to action.
    </p>

    <!-- Results -->
    <div v-if="results.length" class="bd-results mt-3">
      <div v-for="r in results" :key="r.domain" class="bd-result card mt-2">
        <div class="card-body py-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div class="bd-result__name">
            <code>{{ r.domain }}</code>
            <span class="bd-pill" :class="r.available ? 'bd-pill--yes' : 'bd-pill--no'">
              {{ r.available ? 'Available' : 'Taken' }}
            </span>
          </div>
          <div class="bd-result__buy d-flex align-items-center gap-3">
            <span class="bd-price" :title="priceTitle(r.pricing)">{{ r.retail_display }}<span class="text-muted small">/yr</span></span>
            <button
              class="btn btn-outline-primary btn-sm"
              :disabled="!r.available || busy.order === r.domain"
              @click="placeOrder(r)"
            >
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.order === r.domain" />
              <CygnusIcon name="basket-shopping" class="me-1" v-else />Order
            </button>
          </div>
        </div>
        <div v-if="r.pricing" class="bd-breakdown small text-muted px-3 pb-2">
          Transparent pricing: wholesale ${{ fmt(r.pricing.wholesale_usd) }}
          × fx {{ fmt(r.pricing.fx_rate) }} × margin {{ fmt(r.pricing.margin) }}
          → {{ r.retail_display }}
        </div>
      </div>
    </div>
    <p v-else-if="searched && !busy.search" class="text-muted small mt-3">No candidates found for that search.</p>

    <!-- Placed confirmation -->
    <div v-if="placed" class="alert alert-success py-2 small mt-3">
      <CygnusIcon name="check-circle" class="me-1" />{{ placed }}
    </div>

    <!-- My orders -->
    <div class="bd-orders mt-4">
      <div class="d-flex align-items-center justify-content-between">
        <h6 class="mb-0">Your domain orders</h6>
        <button class="btn btn-link btn-sm" :disabled="busy.orders" @click="loadOrders">
          <CygnusIcon name="spinner" :spin="true" v-if="busy.orders" />
          <CygnusIcon name="refresh" v-else />
        </button>
      </div>
      <div v-if="!busy.orders && orders.length === 0" class="text-muted small mt-2">
        No orders yet. Search above to buy your first domain.
      </div>
      <div v-for="o in orders" :key="o.id" class="bd-order card mt-2">
        <div class="card-body py-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div><code>{{ o.domain }}</code> <span class="text-muted small">· £{{ fmt(o.retail_pence / 100) }}/yr</span></div>
          <span class="bd-pill" :class="'bd-pill--' + statusClass(o.status)">{{ statusLabel(o.status) }}</span>
        </div>
        <p v-if="o.status === 'pending_approval'" class="bd-note small px-3 pb-2 mb-0">
          A human reviews every order — we'll set it up automatically once approved.
        </p>
        <p v-else-if="o.error" class="bd-error small px-3 pb-2 mb-0">{{ o.error }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "BuyDomain",
  data() {
    return {
      api: useApi(),
      query: "",
      results: [],
      orders: [],
      searched: false,
      registrarLive: null,
      placed: "",
      error: null,
      busy: { search: false, order: null, orders: false },
    };
  },
  mounted() { this.loadOrders(); },
  methods: {
    async doSearch() {
      const q = this.query.trim();
      if (!q) return;
      this.busy.search = true; this.error = null; this.placed = ""; this.searched = false;
      try {
        const res = await this.api.get(`/api/v1/admin/buy-domain/search?q=${encodeURIComponent(q)}`);
        const payload = res?.data ?? res;
        this.results = Array.isArray(payload?.results) ? payload.results : [];
        this.registrarLive = payload?.registrar_live ?? null;
        this.searched = true;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Search failed.";
      }
      this.busy.search = false;
    },
    async placeOrder(r) {
      this.busy.order = r.domain; this.error = null; this.placed = "";
      try {
        const res = await this.api.post("/api/v1/admin/buy-domain/orders", { domain: r.domain });
        const payload = res?.data ?? res;
        if (payload?.order) {
          this.placed = payload.message || "Order placed — pending operator approval.";
          await this.loadOrders();
        } else {
          this.error = payload?.error || "Could not place the order.";
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Could not place the order.";
      }
      this.busy.order = null;
    },
    async loadOrders() {
      this.busy.orders = true;
      try {
        const res = await this.api.get("/api/v1/admin/buy-domain/orders");
        const payload = res?.data ?? res;
        this.orders = Array.isArray(payload?.data) ? payload.data : [];
      } catch (e) { /* non-fatal */ }
      this.busy.orders = false;
    },
    fmt(n) { return (Math.round((Number(n) || 0) * 100) / 100).toFixed(2); },
    priceTitle(p) {
      if (!p) return "";
      return `Wholesale $${this.fmt(p.wholesale_usd)} × fx ${this.fmt(p.fx_rate)} × margin ${this.fmt(p.margin)}`;
    },
    statusLabel(s) {
      return {
        pending_approval: "Pending approval", approved: "Approved", placed: "Registering",
        complete: "Registered", failed: "Failed", rejected: "Declined",
      }[s] || s;
    },
    statusClass(s) {
      if (s === "complete") return "yes";
      if (s === "failed" || s === "rejected") return "no";
      return "wait";
    },
  },
};
</script>

<style>
.buy-domain { margin-top: var(--m-space-6, 32px); }
.buy-domain__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.buy-domain .bd-result__name { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.buy-domain .bd-price { font-weight: 600; }
.buy-domain .bd-note { color: var(--m-text-secondary, #475569); }
.buy-domain .bd-error { color: var(--m-status-danger, #B91C1C); }
.buy-domain .bd-breakdown { border-top: 1px dashed var(--m-border, #E2E8F0); padding-top: 6px; }
.buy-domain .bd-pill {
  padding: 1px 9px; border-radius: 100px; font-size: 0.7rem; font-weight: 600;
  background: var(--m-bg-subtle, #F1F5F9); color: var(--m-text-secondary, #475569);
}
.buy-domain .bd-pill--yes { background: #DCFCE7; color: #166534; }
.buy-domain .bd-pill--no { background: #FEE2E2; color: #991B1B; }
.buy-domain .bd-pill--wait { background: #FEF9C3; color: #854D0E; }
</style>
